import Button from '@app-studio-web/ui/components/Button'
import Layout from '@app-studio-web/ui/components/Layout'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'
import useUpdateMode from '../../hooks/useUpdateMode'
import { saveAnswer } from '../../utils/onboardingStorage'

// Zodiac signs data with emojis and names
const ZODIAC_SIGNS = [
  { id: 'aries', name: 'Aries', emoji: '♈', element: 'Fire' },
  { id: 'taurus', name: 'Taurus', emoji: '♉', element: 'Earth' },
  { id: 'gemini', name: 'Gemini', emoji: '♊', element: 'Air' },
  { id: 'cancer', name: 'Cancer', emoji: '♋', element: 'Water' },
  { id: 'leo', name: 'Leo', emoji: '♌', element: 'Fire' },
  { id: 'virgo', name: 'Virgo', emoji: '♍', element: 'Earth' },
  { id: 'libra', name: 'Libra', emoji: '♎', element: 'Air' },
  { id: 'scorpio', name: 'Scorpio', emoji: '♏', element: 'Water' },
  { id: 'sagittarius', name: 'Sagittarius', emoji: '♐', element: 'Fire' },
  { id: 'capricorn', name: 'Capricorn', emoji: '♑', element: 'Earth' },
  { id: 'aquarius', name: 'Aquarius', emoji: '♒', element: 'Air' },
  { id: 'pisces', name: 'Pisces', emoji: '♓', element: 'Water' },
]

export default function ZodiacSignPage() {
  const submit = useSubmit()
  const { t } = useTranslation()

  const { isUpdate, postToReactNative } = useUpdateMode()

  const [selectedZodiac, setSelectedZodiac] = useState(4) // Default to Leo (index 4)
  const [exactPosition, setExactPosition] = useState(4) // For smooth thumb movement
  const [isDragging, setIsDragging] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)

  // Use refs to track state in event handlers
  const isDraggingRef = useRef(false)
  const exactPositionRef = useRef(4)

  // Keep refs in sync with state
  useEffect(() => {
    isDraggingRef.current = isDragging
  }, [isDragging])

  useEffect(() => {
    exactPositionRef.current = exactPosition
  }, [exactPosition])

  // Prevent scrolling during touch operations
  useEffect(() => {
    if (isDragging) {
      // Prevent scrolling when dragging
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      // Restore scrolling when not dragging
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isDragging])

  // Global event handlers with inline logic to avoid scope issues
  const handleGlobalMouseMove = (e: MouseEvent) => {
    e.preventDefault()
    if (isDraggingRef.current) {
      // Inline the logic directly here to avoid scope issues
      if (!sliderRef.current) return

      const rect = sliderRef.current.getBoundingClientRect()

      // Clamp the mouse position to the slider bounds
      const clampedX = Math.max(rect.left, Math.min(rect.right, e.clientX))
      const percentage = (clampedX - rect.left) / rect.width
      const newExactPosition = Math.max(
        0,
        Math.min(
          ZODIAC_SIGNS.length - 1,
          percentage * (ZODIAC_SIGNS.length - 1),
        ),
      )

      // Additional safety check - ensure position is never outside bounds
      const finalPosition = Math.max(
        0,
        Math.min(ZODIAC_SIGNS.length - 1, newExactPosition),
      )

      // Always update exact position for smooth thumb movement
      exactPositionRef.current = finalPosition
      setExactPosition(finalPosition)
    }
  }

  const handleGlobalMouseUp = (e: MouseEvent) => {
    e.preventDefault()
    isDraggingRef.current = false
    setIsDragging(false)

    // Snap to nearest zodiac sign when dragging ends
    const snappedIndex = Math.round(exactPositionRef.current)
    if (snappedIndex >= 0 && snappedIndex < ZODIAC_SIGNS.length) {
      setSelectedZodiac(snappedIndex)
      setExactPosition(snappedIndex)
      exactPositionRef.current = snappedIndex
    }

    document.removeEventListener('mousemove', handleGlobalMouseMove)
    document.removeEventListener('mouseup', handleGlobalMouseUp)
  }

  const handleGlobalTouchMove = (e: TouchEvent) => {
    // Don't call preventDefault to avoid passive listener issues
    // Instead, handle the touch event without preventing default
    if (isDraggingRef.current) {
      // Inline the logic directly here to avoid scope issues
      if (!sliderRef.current) return

      const touch = e.touches[0]
      const rect = sliderRef.current.getBoundingClientRect()

      // Clamp the touch position to the slider bounds
      const clampedX = Math.max(rect.left, Math.min(rect.right, touch.clientX))
      const percentage = (clampedX - rect.left) / rect.width
      const newExactPosition = Math.max(
        0,
        Math.min(
          ZODIAC_SIGNS.length - 1,
          percentage * (ZODIAC_SIGNS.length - 1),
        ),
      )

      // Always update exact position for smooth thumb movement
      exactPositionRef.current = newExactPosition
      setExactPosition(newExactPosition)
    }
  }

  const handleGlobalTouchEnd = () => {
    // Don't call preventDefault to avoid passive listener issues
    isDraggingRef.current = false
    setIsDragging(false)

    // Snap to nearest zodiac sign when dragging ends
    const snappedIndex = Math.round(exactPositionRef.current)
    if (snappedIndex >= 0 && snappedIndex < ZODIAC_SIGNS.length) {
      setSelectedZodiac(snappedIndex)
      setExactPosition(snappedIndex)
      exactPositionRef.current = snappedIndex
    }

    document.removeEventListener('touchmove', handleGlobalTouchMove)
    document.removeEventListener('touchend', handleGlobalTouchEnd)
  }

  const updateSliderFromPosition = (clientX: number) => {
    if (!sliderRef.current) return

    const rect = sliderRef.current.getBoundingClientRect()

    // Clamp the click position to the slider bounds
    const clampedX = Math.max(rect.left, Math.min(rect.right, clientX))
    const percentage = (clampedX - rect.left) / rect.width
    const newExactPosition = Math.max(
      0,
      Math.min(ZODIAC_SIGNS.length - 1, percentage * (ZODIAC_SIGNS.length - 1)),
    )

    // Always update exact position for smooth thumb movement
    setExactPosition(newExactPosition)

    // Update selected zodiac when dragging ends or when clicking on track
    const newIndex = Math.round(newExactPosition)
    if (
      newIndex !== selectedZodiac &&
      newIndex >= 0 &&
      newIndex < ZODIAC_SIGNS.length
    ) {
      setSelectedZodiac(newIndex)
    }
  }

  // Handle thumb drag start
  const handleThumbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    isDraggingRef.current = true
    setIsDragging(true)

    // Add global mouse move and up listeners
    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    // Force focus to ensure events are captured
    event.currentTarget.focus()
  }

  const handleThumbTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    // Don't call preventDefault to avoid passive listener issues
    event.stopPropagation()
    isDraggingRef.current = true
    setIsDragging(true)

    // Add global touch move and end listeners
    document.addEventListener('touchmove', handleGlobalTouchMove)
    document.addEventListener('touchend', handleGlobalTouchEnd)
  }

  // Smooth continuous update for range input (fallback)
  const handleRangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value)
    const clampedValue = Math.max(
      0,
      Math.min(ZODIAC_SIGNS.length - 1, Math.round(value)),
    )

    if (clampedValue !== selectedZodiac) {
      setSelectedZodiac(clampedValue)
    }
  }

  // Handle clicking on the slider track
  const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only handle clicks on the slider track, not the thumb
    if (event.target === event.currentTarget) {
      updateSliderFromPosition(event.clientX)
      // Snap to exact position for track clicks
      const rect = sliderRef.current?.getBoundingClientRect()
      if (rect) {
        const percentage = Math.max(
          0,
          Math.min(1, (event.clientX - rect.left) / rect.width),
        )
        const newExactPosition = percentage * (ZODIAC_SIGNS.length - 1)
        const snappedIndex = Math.round(newExactPosition)
        if (snappedIndex >= 0 && snappedIndex < ZODIAC_SIGNS.length) {
          setSelectedZodiac(snappedIndex)
          setExactPosition(snappedIndex)
        }
      }
    }
  }

  const handleTrackTouch = (event: React.TouchEvent<HTMLDivElement>) => {
    // Only handle touches on the slider track, not the thumb
    if (event.target === event.currentTarget) {
      // Don't call preventDefault to avoid passive listener issues
      const touch = event.touches[0]
      updateSliderFromPosition(touch.clientX)
      // Snap to exact position for track touches
      const rect = sliderRef.current?.getBoundingClientRect()
      if (rect) {
        const percentage = Math.max(
          0,
          Math.min(1, (touch.clientX - rect.left) / rect.width),
        )
        const newExactPosition = percentage * (ZODIAC_SIGNS.length - 1)
        const snappedIndex = Math.round(newExactPosition)
        if (snappedIndex >= 0 && snappedIndex < ZODIAC_SIGNS.length) {
          setSelectedZodiac(snappedIndex)
          setExactPosition(snappedIndex)
        }
      }
    }
  }

  // Get current zodiac based on exact position (for real-time updates during drag)
  const getCurrentZodiac = () => {
    const clampedPosition = Math.max(
      0,
      Math.min(ZODIAC_SIGNS.length - 1, exactPosition),
    )
    const index = Math.round(clampedPosition)
    const zodiac = ZODIAC_SIGNS[index]
    return {
      ...zodiac,
      name: t(`pages.onboarding.zodiacSign.signs.${zodiac.id}`),
    }
  }

  // Get safe percentage for thumb positioning (ensures thumb stays within bounds)
  const getSafeThumbPosition = () => {
    const clampedPosition = Math.max(
      0,
      Math.min(ZODIAC_SIGNS.length - 1, exactPosition),
    )
    const percentage = (clampedPosition / (ZODIAC_SIGNS.length - 1)) * 100
    return Math.max(0, Math.min(100, percentage))
  }

  // Get optimal zodiac info position with screen bounds checking
  const getOptimalZodiacInfoPosition = () => {
    const thumbPosition = getSafeThumbPosition()

    // Get the slider container width for more precise calculations
    const sliderWidth = sliderRef.current?.offsetWidth || 327

    // Calculate the actual pixel position of the thumb
    const thumbPixelPosition = (thumbPosition / 100) * sliderWidth

    // Estimate the zodiac info width (emoji + text + padding)
    const infoWidth = 120 // Approximate width of emoji + text

    // Calculate left and right boundaries
    const leftBoundary = infoWidth / 2
    const rightBoundary = sliderWidth - infoWidth / 2

    // If thumb is near left edge, position info to the right
    if (thumbPixelPosition < leftBoundary) {
      return { left: '0%', transform: 'translateX(0%)' }
    }

    // If thumb is near right edge, position info to the left
    if (thumbPixelPosition > rightBoundary) {
      return { right: '0%', transform: 'translateX(0%)' }
    }

    // For middle positions, center the info above the thumb
    return { left: '50%', transform: 'translateX(-50%)' }
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(event.currentTarget)

    const answer = {
      question_key: 'zodiac_sign',
      question: "What's your zodiac sign?",
      answer_key: ZODIAC_SIGNS[selectedZodiac].id,
      answer: ZODIAC_SIGNS[selectedZodiac].name,
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer('zodiac_sign', answer)
      submit(formData, { method: 'post' })
    }
  }

  return (
    <Layout>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="mt-11 shrink-0">
          <h1 className="custom-heading mb-4 text-3xl">
            {t('pages.onboarding.zodiacSign.heading')}
          </h1>
        </div>

        {/* Content */}
        <div className="flex grow items-center justify-center px-6">
          {/* Slider Container */}
          <div className="w-full max-w-sm">
            <div className="relative mb-8" ref={sliderRef}>
              {/* Slider Track - Clickable */}
              <div
                className="relative cursor-pointer rounded bg-gray-400"
                style={{
                  backgroundColor: '#9a9a9a',
                  height: '8px',
                  borderRadius: '4px',
                  opacity: 1,
                  touchAction: 'none',
                }}
                onClick={handleTrackClick}
                onTouchStart={handleTrackTouch}
              ></div>

              {/* Slider Input - Hidden, used as fallback only */}
              <input
                type="range"
                min="0"
                max={ZODIAC_SIGNS.length - 1}
                step="0.1"
                value={selectedZodiac}
                onChange={handleRangeInput}
                onInput={handleRangeInput}
                className="pointer-events-none absolute top-0 left-0 z-0 opacity-0"
                style={{
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  height: '32px',
                }}
              />

              {/* Custom Slider Thumb with Zodiac Info - Draggable */}
              <div
                className={`absolute top-1/2 -translate-y-1/2 transform cursor-grab transition-all ease-out ${
                  isDragging ? 'scale-105 cursor-grabbing' : ''
                } ${isDragging ? 'duration-0' : 'duration-75'}`}
                style={{
                  left: `${getSafeThumbPosition()}%`,
                  maxWidth: '327px',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
                onMouseDown={handleThumbMouseDown}
                onTouchStart={handleThumbTouchStart}
                draggable={false}
              >
                {/* Zodiac Info Above Thumb */}
                <div
                  className={`absolute bottom-8 mb-2 transition-all ease-out ${isDragging ? 'duration-0' : 'duration-150'}`}
                  style={{
                    ...getOptimalZodiacInfoPosition(),
                    minWidth: '120px',
                    maxWidth: '200px',
                  }}
                >
                  <div className="flex flex-row items-center justify-center">
                    {/* Large Emoji */}
                    <div
                      className={`mb-1 text-4xl transition-all ease-out ${isDragging ? 'duration-0' : 'duration-150'}`}
                    >
                      {getCurrentZodiac().emoji}
                    </div>
                    {/* Purple Badge */}
                    <span className="px-2 py-1 whitespace-nowrap">
                      {getCurrentZodiac().name}
                    </span>
                  </div>
                </div>

                {/* Actual Thumb - Draggable Area */}
                <div
                  className={`rounded-full shadow-lg transition-all ease-out ${isDragging ? 'shadow-xl duration-0' : 'duration-75'}`}
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#000000',
                  }}
                  onMouseDown={handleThumbMouseDown}
                  onTouchStart={handleThumbTouchStart}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <Form className="mb-8 shrink-0" method="post" onSubmit={onSubmit}>
          <input
            type="hidden"
            name="answer"
            value={ZODIAC_SIGNS[selectedZodiac].id}
          />
          <div className="flex justify-center">
            <Button
              title={t('pages.onboarding.zodiacSign.continue')}
              type="submit"
              dataTestId="button-continue"
            />
          </div>
        </Form>
      </div>
    </Layout>
  )
}
